import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '../components/ui/button';
import { ZoomIn, ZoomOut, Check, X, RotateCw } from 'lucide-react';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.crossOrigin = 'anonymous';
    img.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}

const ImageCropper = ({ imageUrl, onSave, onCancel, aspect = 16 / 9, title = 'Crop Image' }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedImg(imageUrl, croppedAreaPixels);
      await onSave(blob);
    } catch (err) {
      console.error('Crop failed:', err);
      alert('Failed to crop image');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="image-cropper-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative h-80 sm:h-96 bg-slate-900">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            cropShape="rect"
            showGrid={true}
          />
        </div>

        <div className="px-5 py-4 bg-slate-50 space-y-3">
          <div className="flex items-center gap-3">
            <ZoomOut className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-purple-600"
              data-testid="crop-zoom-slider"
            />
            <ZoomIn className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <button
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="ml-2 p-1.5 rounded-lg hover:bg-slate-200 transition-colors"
              title="Rotate 90°"
            >
              <RotateCw className="w-4 h-4 text-slate-500" />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">Drag to reposition, scroll or use slider to zoom</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onCancel} data-testid="crop-cancel-btn">Cancel</Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="crop-save-btn"
              >
                {saving ? 'Saving...' : <><Check className="w-4 h-4 mr-1" /> Apply</>}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
